import { useCallback, useEffect, useRef, useState } from 'react'
import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent } from 'react'
import { createPortal } from 'react-dom'
import { formatMoney } from './ProjectWorkspace/sections/offerShared'
import { fetchMyTeamInvites, listMyTransfers as fetchMyTransfers, listBookingChanges, listLowStock, listRefundFailed, listReadyAuthCodes, listUnreadSales, listUnreadSubmissions, markBookingChangeSeen, markOrderSeen, openBillingPortal, refreshBilling, setSubmissionStatus } from '../lib/api'
import { useInvitesRevision } from '../lib/teamInviteSignal'
import { transferReviewHref } from '../lib/transferInvite'
import { IconBell, IconBuildingStore, IconCalendarEvent, IconChevronsRight, IconCreditCard, IconMailOpened, IconChecks, IconDots, IconInbox, IconKey, IconMail, IconMessage, IconPackage, IconShoppingCart, IconUserPlus, IconTrash, IconX } from '@tabler/icons-react'
import { useI18n } from '../lib/i18n'

/**
 * The bell: things that happened while the customer was not looking.
 *
 * `past_due` means Stripe took a card that failed and is retrying. The account
 * keeps its plan during that window on purpose — cutting access at the first
 * failure loses customers who would have paid — but the retries end, and a
 * customer who never hears about it simply loses the plan one day. This is the
 * warning that makes the grace period useful.
 *
 * Unread form messages are the other kind: a lead that arrived on a site the
 * customer may not have opened in a week. Both are read here rather than in the
 * project, because the bell sits above any project and a notice you only see
 * once you are already in the right place tells you nothing.
 */

/** El icono de cada tipo de aviso. Uno por clase, en un sitio. */
const NOTICE_ICON = {
  billing: IconCreditCard,
  transfer: IconBuildingStore,
  invite: IconUserPlus,
  authCode: IconKey,
  booking: IconCalendarEvent,
  sale: IconShoppingCart,
  stock: IconPackage,
  newsletter: IconMail,
  form: IconMessage,
} as const

type Notice = {
  key: string
  kind: 'billing' | 'form' | 'newsletter' | 'sale' | 'invite' | 'transfer' | 'authCode' | 'booking' | 'stock'
  projectId?: string
  title: string
  body: string
  /** Con quién es (citas con equipo): cara y nombre al final del texto. */
  person?: { name: string; photoUrl: string | null }
  /** Cuándo pasó. Ausente en avisos que no son un hecho puntual, como el cobro. */
  at?: string
  count: number
  /**
   * Si ya se abrió. Sin ver, no es lo mismo que descartado.
   *
   * Abrir un aviso lo sacaba de la campana para siempre: se marcaba «visto» y la
   * consulta sólo devolvía lo no visto, así que mirar equivalía a borrar. Ahora
   * el visto se queda en la lista, apagado, y sólo la equis —o «borrar todas»—
   * lo quita. Los avisos que no son un hecho puntual (el cobro, una invitación)
   * no tienen visto: están o no están.
   */
  seen?: boolean
  /**
   * Marcarlo como leído sin abrirlo (la ✓ de la fila y «Marcar todas como
   * leídas»). Sólo los que tienen visto en el servidor: mensajes, ventas y citas.
   */
  markSeen?: () => Promise<unknown>
  action?: { label: string; run: () => void }
}

const DISMISSED_NOTICE_STORAGE_KEY = 'veltica:dismissed-message-notifications:v1'

function readDismissedNoticeKeys(): Set<string> {
  try {
    const value = window.localStorage.getItem(DISMISSED_NOTICE_STORAGE_KEY)
    const ids: unknown = value ? JSON.parse(value) : []
    if (!Array.isArray(ids)) return new Set()
    return new Set((ids as unknown[]).filter((id): id is string => typeof id === 'string'))
  } catch {
    return new Set()
  }
}

function saveDismissedNoticeKeys(keys: Set<string>) {
  try {
    // Mantener un límite evita que una bandeja usada durante años haga crecer
    // indefinidamente una preferencia que sólo vive en este navegador.
    window.localStorage.setItem(DISMISSED_NOTICE_STORAGE_KEY, JSON.stringify([...keys].slice(-500)))
  } catch {
    // Si el navegador bloquea storage, descartar sigue funcionando esta sesión.
  }
}

/**
 * Los avisos pendientes sin fecha conservan su prioridad funcional. Entre los
 * que sí representan un hecho, el más reciente siempre va primero.
 */
function sortNoticesNewestFirst(notices: Notice[]): Notice[] {
  return notices.sort((left, right) => {
    if (!left.at && !right.at) return 0
    if (!left.at) return -1
    if (!right.at) return 1
    return new Date(right.at).getTime() - new Date(left.at).getTime()
  })
}

/**
 * La campana del proyecto abierto.
 *
 * Fue de la cuenta entera y eso la volvía inservible en cuanto hay más de un
 * sitio: los mensajes de uno se mezclaban con las ventas de otro, y al pulsar
 * uno te sacaba del proyecto en el que estabas trabajando. El caso que lo
 * destapó fue peor — un proyecto BORRADO seguía mandando avisos, y al abrirlos
 * no llevaban a ninguna parte porque ya no había a dónde ir.
 *
 * Lo de la cuenta sigue saliendo siempre: una invitación, un traspaso, un cobro
 * fallido o un código de dominio no son de un sitio, son de quien los recibe, y
 * esconderlos hasta que entres en «algún» proyecto sería esconderlos y ya.
 */
export function NotificationsMenu({ token, projectId }: { token: string; projectId?: string | undefined }) {
  const { t, fmt, language } = useI18n()
  const [open, setOpen] = useState(false)
  const [closing, setClosing] = useState(false)
  const [actionsOpen, setActionsOpen] = useState(false)
  const [notices, setNotices] = useState<Notice[]>([])
  const invitesRevision = useInvitesRevision()
  /* Arranca en `true`: el panel puede abrirse antes de que respondan las
     consultas, y hasta entonces no se sabe si hay avisos o no. */
  const [loading, setLoading] = useState(true)
  const dismissedNoticeKeysRef = useRef<Set<string>>(readDismissedNoticeKeys())

  const closePanel = useCallback(() => {
    if (closing) return
    setActionsOpen(false)
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setOpen(false)
      return
    }
    setClosing(true)
  }, [closing])

  useEffect(() => {
    let alive = true
    setNotices([])
    setLoading(true)

    /*
     * Las tres consultas siguen siendo independientes —que falle la de
     * facturación no puede esconder los mensajes del cliente, ni al revés— pero
     * se pintan JUNTAS.
     *
     * Antes cada una escribía en cuanto respondía, y el panel crecía a saltos:
     * se abría con «no tienes notificaciones» —mintiendo, porque aún estaba
     * preguntando—, luego daba un tirón al llegar los mensajes y otro al llegar
     * las ventas. `allSettled` conserva la independencia de los fallos y
     * cambia tres saltos por una sola transición desde el hueco.
     */
    const billing = refreshBilling(token).then((state): Notice[] => {
      if (state.status !== 'past_due') return []
      return [{
        key: 'billing',
        kind: 'billing',
        title: t('notif.billingTitle'),
        body: t('notif.billingBody'),
        count: 1,
        action: {
          label: t('notif.billingAction'),
          run: () => { void openBillingPortal(token).then(({ url }) => { window.location.href = url }) },
        },
      }]
    })

    // Un aviso por mensaje: quién escribió y desde qué formulario es lo que
    // permite decidir si abrirlo ahora sin entrar a mirar.
    const messages = listUnreadSubmissions(token, projectId).then((submissions): Notice[] =>
      submissions.filter((submission) => !dismissedNoticeKeysRef.current.has(submission.id)).map((submission) => ({
        key: submission.id,
        projectId: submission.projectId,
        kind: submission.formName.trim().toLocaleLowerCase().includes('newsletter')
          ? 'newsletter' as const
          : 'form' as const,
        title: submission.name.trim() || submission.email.trim() || t('notif.someoneWrote'),
        body: submission.formName,
        at: submission.createdAt,
        count: 1,
        seen: submission.seen,
        markSeen: () => setSubmissionStatus(submission.projectId, submission.id, 'read', token),
        action: {
          // Misma forma de consulta que lee el panel al cargar, más `record`
          // para abrir ESE mensaje en vez de dejarte en la bandeja.
          label: t('notif.viewMessage'),
          run: () => {
            const query = new URLSearchParams({
              project: submission.projectId,
              section: 'forms',
              record: submission.id,
            })
            window.location.href = `${window.location.pathname}?${query.toString()}`
          },
        },
      })),
    )

    /*
     * Las ventas, en su propia consulta.
     *
     * Aquí "sin ver" lo dice el servidor —`seen_at` en el cobro— y no este
     * navegador: una venta es la clase de aviso que se mira desde el teléfono y
     * se abre desde el escritorio, y con la marca guardada aquí volvería a
     * aparecer en el otro aparato como si fuera nueva.
     */
    const sales = listUnreadSales(token, projectId).then((rows): Notice[] =>
      rows.filter((sale) => !dismissedNoticeKeysRef.current.has(sale.id)).map((sale) => ({
        key: sale.id,
        projectId: sale.projectId,
        kind: 'sale' as const,
        // El importe primero: de una venta, lo que se quiere saber es cuánto.
        title: t('notif.saleTitle', { amount: sale.formatted }),
        body: [sale.offerTitle || t('notif.deletedOffer'), sale.buyerName || sale.buyerEmail].filter(Boolean).join(' · '),
        at: sale.createdAt,
        count: 1,
        seen: sale.seen,
        markSeen: () => markOrderSeen(sale.projectId, sale.id, token),
        action: {
          label: t('notif.viewSale'),
          run: () => {
            const query = new URLSearchParams({
              project: sale.projectId,
              section: 'orders',
              record: sale.id,
            })
            window.location.href = `${window.location.pathname}?${query.toString()}`
          },
        },
      })),
    )

    /*
     * Las invitaciones a un equipo.
     *
     * Van en la campana y no sólo en el correo porque el correo puede no llegar
     * —en local no hay clave de Resend, y en producción está el spam— y sin esto
     * la invitación sólo existe en un enlace que quizá nadie ve. Aquí aparece
     * para quien ya tiene sesión abierta, que es el caso más común: casi siempre
     * se invita a alguien que ya usa véltica.
     *
     * No se pueden descartar como los mensajes: no son un aviso de algo que pasó
     * sino algo pendiente de responder, y esconderlo dejaría un asiento ocupado
     * sin rastro de por qué. Se van solas al aceptar, rechazar o caducar.
     */
    const invites = fetchMyTeamInvites(token).then((rows): Notice[] =>
      rows.map((invite) => ({
        key: `invite:${invite.id}`,
        kind: 'invite' as const,
        title: invite.fromName
          ? t('notif.inviteFrom', { name: invite.fromName, project: invite.projectName })
          : t('notif.invite', { project: invite.projectName }),
        body: t('notif.inviteBody', { role: t(invite.roleLabelKey).toLocaleLowerCase() }),
        /* Sin `at` a propósito: eso es lo que distingue «pasó algo» de «hay algo
           pendiente», y decide el trazado. Con fecha, el aviso sale con botón de
           descartar — y descartar una invitación sólo la escondería hasta la
           siguiente recarga, porque el descarte sólo se guarda para los mensajes
           de formulario. Un botón que no cumple es peor que no tenerlo. */
        count: 1,
        action: {
          /* Lleva a decidir, no decide: aceptar desde una notificación da acceso
             a un proyecto sin haber visto a qué, y eso no es un clic de campana. */
          label: t('notif.inviteAction'),
          run: () => { window.location.href = window.location.pathname },
        },
      })),
    )

    /*
     * Los traspasos de proyecto.
     *
     * Por lo mismo que las invitaciones al equipo: viven en un correo que puede
     * no llegar, y sin esto quien entrega se queda esperando una respuesta que
     * nunca se pidió porque el aviso no existió. Tampoco se descartan —hay algo
     * pendiente de responder— y se van solos al aceptar, rechazar o caducar.
     */
    const transfers = fetchMyTransfers(token).then((rows): Notice[] =>
      rows.map((transfer) => ({
        key: `transfer:${transfer.id}`,
        kind: 'transfer' as const,
        title: transfer.fromName
          ? t('notif.transferFrom', { name: transfer.fromName, project: transfer.projectName })
          : t('notif.transfer', { project: transfer.projectName }),
        body: t('notif.transferBody'),
        count: 1,
        action: {
          /* Lleva a ver qué se recibe, no a aceptar: asumir un sitio ajeno con
             sus datos personales de terceros no es un clic de campana.

             A la dirección del traspaso y no al panel: desde la campana se puede
             estar en cualquier sección, y volver al inicio esperando que el
             banner aparezca deja el aviso sin destino en todas las demás. */
          label: t('notif.transferAction'),
          run: () => { window.location.href = transferReviewHref(transfer.id) },
        },
      })),
    )

    /*
     * El código de transferencia, ya entregado.
     *
     * Va en la campana porque es una respuesta a algo que la persona PIDIÓ: se
     * queda esperando y no tiene forma de saber cuándo llegó, salvo abriendo los
     * ajustes de su dominio a ver si ya está. El correo también sale, pero el
     * correo se pierde.
     *
     * Sin el código en el aviso: se dice que está listo y se lleva a donde se
     * lee. Una credencial de transferencia no viaja en una lista que el panel
     * pide sola en cada carga.
     */
    const codes = listReadyAuthCodes(token).then((rows): Notice[] =>
      rows.filter((row) => !dismissedNoticeKeysRef.current.has(`authcode-${row.id}`)).map((row) => ({
        key: `authcode-${row.id}`,
        kind: 'authCode' as const,
        title: t('notif.authCodeTitle'),
        body: row.hostname,
        at: row.resolvedAt ?? new Date().toISOString(),
        count: 1,
        action: {
          label: t('notif.authCodeAction'),
          run: () => { window.location.href = `${window.location.pathname}?view=domains` },
        },
      })),
    )

    /*
     * Los cambios que hacen los clientes en sus propias citas.
     *
     * Cancelar o mover pasa sin que nadie esté delante, y hasta ahora sólo se
     * avisaba por correo — que se pierde entre otros cincuenta. Aquí llega al
     * sitio donde se va a actuar.
     *
     * La hora se escribe en la zona de la CITA y no en la de quien mira: es la
     * hora a la que el negocio tiene que estar, y traducirla al huso del que
     * abre el panel desde otro país diría una hora a la que no pasa nada.
     */
    const bookingChanges = listBookingChanges(token, projectId).then((rows): Notice[] =>
      rows.filter((row) => !dismissedNoticeKeysRef.current.has(`booking-${row.id}`)).map((row) => {
        /* El huso sigue siendo el de la cita —es la hora a la que el negocio
           tiene que estar— pero el idioma es el de quien lee. Estaba fijo en
           `es-MX`, así que el panel en inglés decía «14 de marzo». */
        const when = new Intl.DateTimeFormat(language, {
          timeZone: row.timezone, day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
        }).format(new Date(row.startsAt))
        const quien = row.customerName.trim() || t('notif.someClient')
        return {
          key: `booking-${row.id}`,
          projectId: row.projectId,
          kind: 'booking' as const,
          title: row.kind === 'cancelled'
            ? t('notif.bookingCancelled', { who: quien })
            : row.kind === 'rescheduled'
              ? t('notif.bookingMoved', { who: quien })
              : t('notif.bookingNew', { who: quien }),
          /* El motivo, si lo escribió: es lo único de este aviso que no se
             deduce del estado, y es lo que decide si hay que hacer algo. */
          body: [row.offerTitle || t('notif.booking'), when, row.cancellationReason]
            .filter(Boolean)
            .join(' · '),
          ...(row.withName ? { person: { name: row.withName, photoUrl: row.withPhoto ?? null } } : {}),
          at: row.updatedAt,
          count: 1,
          seen: row.seen,
          markSeen: () => markBookingChangeSeen(row.id, token, row.projectId),
          action: {
            label: row.kind === 'rescheduled' ? t('notif.viewNewTime') : t('notif.viewBooking'),
            run: () => {
              const url = new URL(window.location.href)
              url.searchParams.set('project', row.projectId)
              url.searchParams.set('section', 'bookingsList')
              url.searchParams.set('record', row.id)
              window.location.href = url.toString()
            },
          },
        }
      }),
    )

    /*
     * Los productos físicos con pocas unidades o agotados.
     *
     * Es un estado, no un hecho: se va solo al reponer. La clave lleva cuántas
     * quedan, así que descartarlo con la equis lo calla hasta que baje otra
     * unidad, y entonces vuelve —que es justo cuando hace falta otra vez—.
     */
    /* Con variantes, cada combinación avisa por su cuenta: «Taza · M / Verde». */
    const stockKey = (row: { offerId: string; variantId: string | null; stock: number }) => `stock-${row.variantId ?? row.offerId}-${row.stock}`
    const lowStock = listLowStock(token, projectId).then((rows): Notice[] =>
      rows.filter((row) => !dismissedNoticeKeysRef.current.has(stockKey(row))).map((row) => {
        const name = [row.title || t('notif.deletedOffer'), row.variant].filter(Boolean).join(' · ')
        return {
          key: stockKey(row),
          projectId: row.projectId,
          kind: 'stock' as const,
          title: row.stock <= 0
            ? t('notif.stockOut', { name })
            : t('notif.stockLow', { name, count: row.stock }),
          body: t(row.stock <= 0 ? 'notif.stockOutBody' : 'notif.stockLowBody'),
          at: row.at,
          count: 1,
          action: {
            label: t('notif.restock'),
            run: () => {
              const query = new URLSearchParams({ project: row.projectId, section: 'physicalProducts', record: row.offerId })
              window.location.href = `${window.location.pathname}?${query.toString()}`
            },
          },
        }
      }),
    )

    /*
     * Los reembolsos automáticos que se rindieron tras sus 24 intentos. Un
     * estado, como las existencias: se va solo cuando el dueño lo devuelve en
     * Stripe. El correo sale una vez; esto queda hasta que se resuelva.
     */
    const refundsFailed = listRefundFailed(token, projectId).then((rows): Notice[] =>
      rows.filter((row) => !dismissedNoticeKeysRef.current.has(`refund-${row.id}`)).map((row) => ({
        key: `refund-${row.id}`,
        projectId: row.projectId,
        kind: 'booking' as const,
        title: t('notif.refundFailed', { amount: formatMoney(row.refundAmount ?? 0, row.currency ?? 'usd') }),
        body: [row.offerTitle || t('notif.booking'), row.customerName, t('notif.refundFailedBody')].filter(Boolean).join(' · '),
        at: row.updatedAt,
        count: 1,
        action: {
          label: t('notif.viewBooking'),
          run: () => {
            const query = new URLSearchParams({ project: row.projectId, section: 'bookingsList', record: row.id })
            window.location.href = `${window.location.pathname}?${query.toString()}`
          },
        },
      })),
    )

    // El cobro fallido va primero: es lo único que caduca. `allSettled` porque
    // una consulta caída no es un aviso, y tampoco puede tumbar a las otras.
    void Promise.allSettled([billing, invites, transfers, codes, bookingChanges, refundsFailed, lowStock, messages, sales]).then((results) => {
      if (!alive) return
      setNotices(sortNoticesNewestFirst(
        results.flatMap((result) => result.status === 'fulfilled' ? result.value : []),
      ))
      setLoading(false)
    })

    return () => { alive = false }
    /* `invitesRevision` como dependencia: es lo que hace que responder una
       invitación desde el banner del panel vuelva a preguntar aquí. Sin ella
       este efecto sólo corría al montar, así que el aviso sobrevivía a su propia
       respuesta.

       Y `t` con `language`: el texto de cada aviso se arma aquí, al recibirlo,
       así que queda escrito en el idioma que hubiera entonces. Volver a pedir
       al cambiar de idioma cuesta una consulta que casi nadie dispara —el
       idioma se cambia una vez— y es lo que evita una campana a medias.

       Y `projectId` por lo mismo que se le pasa a las tres consultas: sin él la
       campana seguía contestando por el proyecto anterior al cambiar de uno a
       otro sin desmontarse. */
  }, [token, projectId, invitesRevision, t, language])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      closePanel()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [closePanel, open])

  useEffect(() => {
    if (!closing) return
    const timer = window.setTimeout(() => {
      setOpen(false)
      setClosing(false)
    }, 240)
    return () => window.clearTimeout(timer)
  }, [closing])

  const dismissNotice = (notice: Notice) => {
    // Sólo oculta el aviso. El envío, venta o cita original permanece intacto.
    dismissedNoticeKeysRef.current.add(notice.key)
    saveDismissedNoticeKeys(dismissedNoticeKeysRef.current)
    setNotices((current) => current.filter((item) => item.key !== notice.key))
  }

  /* Leído, sin abrirlo: se apaga aquí al momento (y deja de contar en la
     campana) y se guarda en el servidor, como al abrir su ficha. Si falla, se
     vuelve a encender. */
  const markNoticesSeen = (targets: Notice[]) => {
    const pending = targets.filter((notice) => notice.seen === false && notice.markSeen)
    if (!pending.length) return
    const keys = new Set(pending.map((notice) => notice.key))
    setNotices((current) => current.map((notice) => (keys.has(notice.key) ? { ...notice, seen: true } : notice)))
    for (const notice of pending) {
      notice.markSeen!().catch(() => {
        setNotices((current) => current.map((item) => (item.key === notice.key ? { ...item, seen: false } : item)))
      })
    }
  }
  const markAllSeen = () => {
    markNoticesSeen(notices)
    setActionsOpen(false)
  }

  const clearMessageNotifications = () => {
    const messages = notices.filter((notice) => notice.projectId)
    if (messages.length === 0) return
    messages.forEach((notice) => dismissedNoticeKeysRef.current.add(notice.key))
    saveDismissedNoticeKeys(dismissedNoticeKeysRef.current)
    const clearedKeys = new Set(messages.map((notice) => notice.key))
    setNotices((current) => current.filter((notice) => !clearedKeys.has(notice.key)))
    setActionsOpen(false)
  }

  /* Sólo lo que no se ha visto: con los vistos dentro, la campana seguiría
     marcando «3» sobre tres avisos ya leídos y el número dejaría de querer
     decir nada. */
  const notificationCount = notices.reduce((total, notice) => total + (notice.seen ? 0 : notice.count), 0)
  const badgeLabel = notificationCount > 99 ? '99+' : String(notificationCount)

  return (
    <>
      <div className="dash-notifications">
        <button
          className={`dash-topbar-btn${open ? ' is-active' : ''}`}
          type="button"
          aria-label={notificationCount > 0 ? t('notif.bellAriaCount', { count: notificationCount }) : t('notif.bellAria')}
          aria-expanded={open}
          onClick={() => {
            if (open) closePanel()
            else setOpen(true)
            setActionsOpen(false)
          }}
        >
          <IconBell size={18} />
          {/* Announced through the button label above; the visual badge is hidden
              from screen readers so the count is not repeated. */}
          {notificationCount > 0 ? <span className="dash-notifications-dot" aria-hidden="true">{badgeLabel}</span> : null}
        </button>
      </div>

      {open && typeof document !== 'undefined' ? createPortal(
        <div
          className={`dash-modal-backdrop dash-notifications-backdrop${closing ? ' is-closing' : ''}`}
          onPointerDown={(event) => {
            if (event.target === event.currentTarget) closePanel()
          }}
        >
          <section className={`dash-notifications-panel${closing ? ' is-closing' : ''}`} role="dialog" aria-modal="true" aria-label={t('notif.title')}>
          <div className="dash-notifications-panel-head">
            <button
              type="button"
              className="dash-notifications-close"
              aria-label={t('common.close')}
              data-tooltip={t('common.close')}
              onClick={closePanel}
            >
              {/* Dos dibujos y no uno: las flechas dicen «empújalo de vuelta al
                  borde», que es cierto en escritorio —el panel es una gaveta
                  que sale de ahí— y deja de serlo en el teléfono, donde ya no
                  es una gaveta sino un globo flotante y lo que se pulsa para
                  cerrar cualquier cosa así es una equis. El CSS del ancho
                  decide cuál de las dos se ve. */}
              <IconChevronsRight className="dash-notifications-close-icon is-wide" size={17} aria-hidden="true" />
              <IconX className="dash-notifications-close-icon is-narrow" size={17} aria-hidden="true" />
            </button>
            <div className="dash-notifications-panel-title">
              <h2 className="dash-card-title">{t('notif.title')}</h2>
              <span className="dash-card-meta">
                {loading
                  ? t('notif.loading')
                  : notificationCount === 0
                    ? t('notif.allClear')
                    : notificationCount === 1
                      ? t('notif.newOne')
                      : t('notif.newMany', { count: notificationCount })}
              </span>
            </div>
            <div className="dash-notifications-actions-menu">
              <button
                type="button"
                className="dash-notifications-more"
                aria-label={t('notif.moreActions')}
                aria-expanded={actionsOpen}
                onClick={() => setActionsOpen((value) => !value)}
              >
                <IconDots size={17} aria-hidden="true" />
              </button>
              {actionsOpen ? (
                <div className="dash-notifications-menu" role="menu">
                  <button type="button" role="menuitem" disabled={!notices.some((notice) => notice.seen === false && notice.markSeen)} onClick={markAllSeen}>
                    <IconChecks size={15} aria-hidden="true" /> {t('notif.markAllRead')}
                  </button>
                  <button type="button" role="menuitem" disabled={!notices.some((notice) => notice.projectId)} onClick={clearMessageNotifications}>
                    <IconTrash size={15} aria-hidden="true" /> {t('notif.clear')}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
          <div className="dash-notifications-list">
          {loading ? (
            /* Tres filas en hueco, con la forma exacta de un aviso de mensaje:
               el panel se abre ya a su alto y lo que cambia después es el
               contenido, no la caja. Antes se abría con el texto de «no tienes
               notificaciones» —dos renglones— y daba un tirón al llegar los
               datos. Tres porque es lo que suele haber; con más, el panel
               abriría más alto de lo que acabará midiendo. */
            <div className="dash-notifications-loading" aria-hidden="true">
              {[0, 1, 2].map((row) => (
                <div className="dash-notification-skeleton" key={row}>
                  <span className="sk dash-notification-skeleton-icon" />
                  <span className="sk dash-notification-skeleton-title" />
                  <span className="sk dash-notification-skeleton-body" />
                </div>
              ))}
            </div>
          ) : notices.length === 0 ? (
            /* El mismo vacío que el resto del panel —`dash-placeholder`— y no
               uno propio: material, tamaño del icono y peso del texto salen de
               ahí, así que este hueco no puede volver a separarse de los otros.
               `--inset` porque ya vive dentro del panel emergente: la caja del
               vacío sería un segundo marco a unos píxeles del primero.

               Y una bandeja, no la campana. La campana es el botón que abre
               esto: repetida dentro dice «notificaciones», que es lo que ya se
               sabe. Una bandeja vacía dice lo que hay que decir — que no hay
               nada dentro. */
            <div className="dash-placeholder dash-placeholder--inset dash-notifications-empty">
              <IconInbox size={34} aria-hidden="true" />
              <strong>{t('notif.allClear')}</strong>
              <span>{t('notif.emptyBody')}</span>
            </div>
          ) : notices.map((notice) => {
            /* Una escalera de nueve niveles se lee peor que un mapa. Con el
               código de transferencia entrando como séptimo tipo, la cadena de
               ternarios pasaba a tener más sangría que contenido. */
            const TypeIcon = NOTICE_ICON[notice.kind] ?? IconMessage

            /*
             * La tarjeta entera abre el aviso.
             *
             * El ojo se queda: dice QUÉ va a pasar al pulsar —«ver el mensaje»,
             * «ver la venta»— y es el destino del tabulador. Pero exigir puntería
             * sobre un icono de quince píxeles cuando hay una fila de cincuenta
             * de alto delante es pedir trabajo de más; lo natural es pulsar lo
             * que se está mirando.
             *
             * `role="button"` y no un <button> de verdad: dentro ya hay dos
             * botones, y un botón dentro de otro no es HTML válido — el
             * navegador lo desarma y se pierden los dos.
             */
            const open = notice.action?.run
            return (
              <article
                className={`dash-notification dash-loaded-content-in ${notice.at ? 'is-message' : 'is-billing'} is-${notice.kind}${notice.seen === false ? ' is-unseen' : ''}${open ? ' is-openable' : ''}`}
                key={notice.key}
                {...(open ? {
                  role: 'button',
                  tabIndex: 0,
                  onClick: (event: ReactMouseEvent<HTMLElement>) => {
                    /* Lo que se pulsó tiene su propia acción —descartar, o el
                       ojo— y ya la hizo: abrir además sería hacer dos cosas
                       con un clic. */
                    if ((event.target as HTMLElement).closest('button, a')) return
                    open()
                  },
                  onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => {
                    if (event.target !== event.currentTarget) return
                    if (event.key !== 'Enter' && event.key !== ' ') return
                    event.preventDefault()
                    open()
                  },
                } : {})}
              >
                <span className="dash-notification-type-icon dash-widget-glass-icon" aria-hidden="true">
                  <TypeIcon size={15} strokeWidth={2} />
                </span>
                <div className="dash-notification-head">
                  <strong>{notice.title}</strong>
                  {notice.at ? <span className="dash-notification-time">{fmt.relative(notice.at)}</span> : null}
                </div>
                {/* La persona va DENTRO de la línea: el aviso es una cuadrícula
                    con sitio fijo para cada pieza, y una más suelta la rompía. */}
                <p>
                  {notice.body}
                  {notice.person ? (
                    <span className="dash-notif-person">
                      <span aria-hidden="true">·</span>
                      <span className="dash-booking-artist-face" aria-hidden="true">
                        {notice.person.photoUrl ? <img src={notice.person.photoUrl} alt="" /> : notice.person.name.trim().split(/\s+/).slice(0, 2).map((part) => part[0] ?? '').join('').toUpperCase()}
                      </span>
                      {notice.person.name}
                    </span>
                  ) : null}
                </p>
                {notice.action ? notice.at ? (
                  <span className="dash-notification-actions">
                    {/* Marcar como leída, sin abrirla (abrir es pulsar la tarjeta).
                        Sólo mientras está sin ver: ya leída, sobra. */}
                    {notice.seen === false && notice.markSeen ? (
                      <button type="button" className="is-read" aria-label={t('notif.markReadAria', { title: notice.title })} title={t('notif.markRead')} onClick={() => markNoticesSeen([notice])}>
                        <IconMailOpened size={15} stroke={1.8} aria-hidden="true" />
                        <span>{t('notif.markRead')}</span>
                      </button>
                    ) : null}
                    <button type="button" className="is-dismiss" aria-label={t('notif.dismissAria', { title: notice.title })} title={t('notif.dismiss')} onClick={() => dismissNotice(notice)}>
                      <IconTrash size={15} stroke={1.8} aria-hidden="true" />
                      <span>{t('notif.dismissShort')}</span>
                    </button>
                  </span>
                ) : (
                  <button type="button" onClick={notice.action.run}>{notice.action.label}</button>
                ) : null}
              </article>
            )
          })}
          </div>
          </section>
        </div>,
        document.body,
      ) : null}
    </>
  )
}
